import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import {
  FaIdCard,
  FaCheck,
  FaHandshake,
  FaCalendarDay,
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaPhoneAlt,
  FaUser,
  FaMoneyBill,
  FaCreditCard
} from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import {
  employeeService,
  handoverService
} from "../../services/api";
import AllHandoversModal from '../modals/AllHandoversModal';
import HandoverDetailsModal from '../modals/HandoverDetailsModal';
import EmployeeFormModal from '../modals/EmployeeFormModal';
const MySwal = withReactContent(Swal);

const EmployeeManagement = () => {  // State
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [creatingEmployee, setCreatingEmployee] = useState(false);
  const [showEmployeeFormModal, setShowEmployeeFormModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAllHandoversModal, setShowAllHandoversModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedHandoverId, setSelectedHandoverId] = useState(null);
  const [showHandoverDetailsModal, setShowHandoverDetailsModal] = useState(false);
  const [error, setError] = useState(null);

  // Fetch employees on component mount
  useEffect(() => {
    fetchEmployees();
  }, []);

  // Fetch all employees
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeService.getAllEmployees();
      setEmployees(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to load employees. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  // Handle employee creation
  const handleEmployeeCreate = () => {
    setSelectedEmployee(null);
    setShowEmployeeFormModal(true);
  };

  // Handle employee edit
  const handleEmployeeEdit = (employeeId) => {
    try {
      // Find the employee
      const employee = employees.find((e) => e._id === employeeId);
      if (!employee) return;
      
      setSelectedEmployee(employee);
      setShowEmployeeFormModal(true);
    } catch (error) {
      console.error('Error preparing to edit employee:', error);
    }
  };

  // Handle form submission (create or update)
  const handleEmployeeFormSubmit = async (formData) => {
    try {
      setCreatingEmployee(true);
      
      if (selectedEmployee) {
        // Update existing employee
        await employeeService.updateEmployee(selectedEmployee._id, formData);
        
        await MySwal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Employee updated successfully',
          confirmButtonColor: '#1e3a8a',
        });
      } else {
        // Create new employee
        await employeeService.registerEmployee(formData);
        
        await MySwal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Employee created successfully',
          confirmButtonColor: '#1e3a8a',
        });
      }

      // Close modal and refresh employees
      setShowEmployeeFormModal(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      await MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to save employee. Please try again.',
        confirmButtonColor: '#1e3a8a',
      });
    } finally {
      setCreatingEmployee(false);
    }
  };

  // Handle employee delete
  const handleEmployeeDelete = async (employeeId) => {
    try {
      const { isConfirmed } = await MySwal.fire({
        title: 'Are you sure?',
        text: 'This will permanently delete this employee. This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#1e3a8a',
        confirmButtonText: 'Yes, delete it!',
      });

      if (!isConfirmed) {
        return;
      }

      // Check if employee has any handovers in progress
      const employee = employees.find(e => e._id === employeeId);
      if (employee && employee.handovers && employee.handovers.length > 0) {
        await MySwal.fire({
          icon: 'error',
          title: 'Cannot Delete Employee',
          text: `This employee has ${employee.handovers.length} handovers associated with them. Please reassign or complete these handovers first.`,
          confirmButtonColor: '#1e3a8a',
        });
        return;
      }

      await employeeService.deleteEmployee(employeeId);

      await MySwal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Employee has been deleted.',
        confirmButtonColor: '#1e3a8a',
      });

      // Refresh data
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      await MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to delete employee. Please try again.',
        confirmButtonColor: '#1e3a8a',
      });
    }
  };  // View employee handovers
  const viewEmployeeHandovers = async (employeeId) => {
    try {
      const employee = employees.find(e => e._id === employeeId);
      console.log('Employee:', employee); 
      if (!employee) return;

      // Set the selected employee ID to trigger showing the modal
      setSelectedEmployeeId(employeeId);
      setShowAllHandoversModal(true);
    } catch (error) {
      console.error('Error preparing to show handovers:', error);
      await MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to prepare handover data. Please try again.',
        confirmButtonColor: '#1e3a8a',
      });
    }
  };

  // Handle viewing a specific handover
  const handleViewHandover = (handoverId) => {
    setSelectedHandoverId(handoverId);
    setShowHandoverDetailsModal(true);
  };

  if (loading && employees.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-800 p-4 rounded-md my-4">
        {error}
        <button 
          onClick={fetchEmployees}
          className="ml-4 bg-red-200 hover:bg-red-300 text-red-800 px-3 py-1 rounded"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Employees Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-blue-400 bg-opacity-40">
              <FaIdCard className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">
                Total Employees
              </p>
              <p className="text-2xl font-bold text-white">
                {employees.length}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
              <FaCheck className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">Active</p>
              <p className="text-2xl font-bold text-white">
                {employees.filter((e) => e.isActive).length}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-yellow-500 to-yellow-700 p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-yellow-400 bg-opacity-40">
              <FaHandshake className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">
                Active Handovers
              </p>
              <p className="text-2xl font-bold text-white">
                {employees.reduce((count, employee) => count + (employee.handovers ? employee.handovers.length : 0), 0)}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-full bg-purple-400 bg-opacity-40">
              <FaCalendarDay className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-white text-sm opacity-80">Recently Added</p>
              <p className="text-2xl font-bold text-white">
                {employees.filter(
                  (e) => new Date(e.createdAt) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                ).length}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search and Add Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search employees..."
              value={employeeSearch}
              onChange={(e) => setEmployeeSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>

          <div>
            <motion.button
              onClick={handleEmployeeCreate}
              disabled={creatingEmployee}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
            >
              {creatingEmployee ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Creating...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <FaPlus className="mr-2" />
                  <span>Add New Employee</span>
                </div>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Employees List */}
      <div className="grid grid-cols-1 gap-6">
        <AnimatePresence>
          {employees
            .filter((employee) => {
              const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
              const searchTerms = employeeSearch.toLowerCase();
              return (
                fullName.includes(searchTerms) ||
                employee.email.toLowerCase().includes(searchTerms) ||
                employee.phoneNumber.toLowerCase().includes(searchTerms) ||
                employee.idNumber.toLowerCase().includes(searchTerms)
              );
            })
            .sort((a, b) => {
              // Sort by active status first, then by name
              if (a.isActive && !b.isActive) return -1;
              if (!a.isActive && b.isActive) return 1;
              return a.firstName.localeCompare(b.firstName);
            })
            .map((employee) => (
              <motion.div
                key={employee._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={`bg-white rounded-lg shadow-md overflow-hidden border-l-4 ${
                  employee.isActive
                    ? "border-green-500"
                    : "border-red-500"
                }`}
              >
                <div className="flex flex-col lg:flex-row">
                  <div className="p-5 flex-grow">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-800">
                        {employee.firstName} {employee.lastName}
                      </h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEmployeeEdit(employee._id)}
                          className="p-2 text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                          title="Edit Employee"
                        >
                          <FaEdit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEmployeeDelete(employee._id)}
                          className="p-2 text-red-700 hover:bg-red-50 rounded-full transition-colors"
                          title="Delete Employee"
                        >
                          <FaTrash className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center mb-2">
                          <FaEnvelope className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-gray-600">{employee.email}</span>
                        </div>
                        <div className="flex items-center mb-2">
                          <FaPhoneAlt className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-gray-600">{employee.phoneNumber}</span>
                        </div>
                        <div className="flex items-center mb-2">
                          <FaIdCard className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-gray-600">ID: {employee.idNumber}</span>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center mb-2">
                          <FaUser className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-gray-600">SSN: {employee.socialSecurityNumber}</span>
                        </div>
                        <div className="flex items-center mb-2">
                          <FaMoneyBill className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-gray-600">Bank: {employee.bankName}</span>
                        </div>
                        <div className="flex items-center mb-2">
                          <FaCreditCard className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-gray-600">Account: {employee.bankAccountNumber}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex justify-between">
                      <div className="flex items-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          employee.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {employee.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <button
                          onClick={() => viewEmployeeHandovers(employee._id)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <FaHandshake className="mr-1" /> 
                          Handovers
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
        </AnimatePresence>

        {employees.filter((employee) => {
          const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
          return (
            fullName.includes(employeeSearch.toLowerCase()) ||
            employee.email.toLowerCase().includes(employeeSearch.toLowerCase()) ||
            employee.phoneNumber.toLowerCase().includes(employeeSearch.toLowerCase()) ||
            employee.idNumber.toLowerCase().includes(employeeSearch.toLowerCase())
          );
        }).length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <p className="text-gray-500">No employees found matching your search.</p>
          </div>        )}
      </div>

      {/* Modal for viewing all handovers */}
      <AnimatePresence>
        {showAllHandoversModal && (
          <AllHandoversModal
            employeeId={selectedEmployeeId}
            onClose={() => setShowAllHandoversModal(false)}
            onViewHandover={handleViewHandover}
          />
        )}
      </AnimatePresence>      {/* Modal for viewing handover details */}
      <AnimatePresence>
        {showHandoverDetailsModal && (
          <HandoverDetailsModal
            handoverId={selectedHandoverId}
            onClose={() => setShowHandoverDetailsModal(false)}
            onDelete={() => {
              setShowHandoverDetailsModal(false);
              if (showAllHandoversModal) {
                // Refresh the handovers list in the AllHandoversModal
                setShowAllHandoversModal(true);
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Modal for creating or editing employees */}
      <AnimatePresence>
        {showEmployeeFormModal && (
          <EmployeeFormModal
            employee={selectedEmployee}
            onClose={() => setShowEmployeeFormModal(false)}
            onSubmit={handleEmployeeFormSubmit}
            isSubmitting={creatingEmployee}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeManagement;
